import { Injectable } from '@angular/core';
import { EstimateSellPrice } from '../../models/dtos';
import { CONSTANTS } from '../../Constants/Constants';
import { SellPriceConfig, TaxTypes } from '../../models/sol-estimate-table/SellPriceConfig';
import { CommonDataService } from '../common-data.service';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import { EstimateUiModel } from '../../models/sol-estimate-table/ui-models/index';

/**
 * Responsible for syncing model-calculated values back into the DTO models
 * stored in CommonDataService so that other consumers (reports, save pipeline, etc.)
 * see the latest data without a round-trip.
 *
 * Reads from EstimateUiModel — no FormGroup references.
 *
 * Updates three DTO objects:
 *  • EstimateGrossMargin  — per-row GM% values
 *  • EstimateSellPrice    — per-row sell price values
 *  • EstimateTab          — flags and aggregate sell prices
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateDtoSyncService {

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService
  ) { }

  /**
   * Master sync — writes all model-calculated values back to DTOs.
   */
  updateModelStateToDTOs(
    model: EstimateUiModel,
    config: SellPriceConfig
  ): void {
    if (config.isMarkUpSelected) {
      this.updateEstimateMarkupPerDTO(model, config);
    } else {
      this.updateEstimateGrossMarginDTO(model, config);
    }
    this.updateEstimateSellPriceDTO(model);
    this.updateEstimateTabDTO(model, config);
  }

  /**
   * At zero cost (hidden root or an empty estimate) only Fixed Price overwrites the detail-row margins on screen
   * (0%), so only its margins are unsafe to persist. Fixed Margin rows show the saved global margin, and Line Item
   * rows keep their own margin, so what is shown is also what has to be saved, otherwise Line Item would reload
   * margins from the previous mode after a pricing mode switch.
   */
  private shouldSkipGMPersistence(model: EstimateUiModel, config: SellPriceConfig): boolean {
    const isZeroCost = model.subTotals.cost === CONSTANTS.ZERO;
    return (this.commonDataService.getRootNodeHidden() || isZeroCost) && config.isFixedPriceSelected === true;
  }

  /**
   * Writes per-row GM% values from the model back into OtherMetaData.grossMargin.
   */
  private updateEstimateGrossMarginDTO(
    model: EstimateUiModel,
    config: SellPriceConfig
  ): void {
    // Zero cost zeroes the row margins; persisting the resulting 0% would
    // destroy the user's margins, so keep the previously stored percentages.
    if (this.shouldSkipGMPersistence(model, config)) return;

    const otherMetaData = this.commonDataService.getOtherMetaData();
    if (!otherMetaData?.grossMargin) return;
    const gm = otherMetaData.grossMargin;

    // ── Material GM% ────────────────────────────────────────────
    const mat = model.materialCosts;
    gm.siemensMaterialGMPercent = mat.siemensMaterial.headerRow.grossMarginPercentage;
    gm.siemensMaterialShippingHandlingGMPercent = mat.siemensMaterial.shippingAndHandling.grossMarginPercentage;
    gm.siemensMaterialWarrantyGMPercent = mat.siemensMaterial.warranty.grossMarginPercentage;

    gm.outsideMaterialGMPercent = mat.outsideMaterial.headerRow.grossMarginPercentage;
    gm.outsideMaterialShippingHandlingGMPercent = mat.outsideMaterial.shippingAndHandling.grossMarginPercentage;
    gm.outsideMaterialWarrantyGMPercent = mat.outsideMaterial.warranty.grossMarginPercentage;

    gm.mechanicalMaterialGMPercent = mat.mechanicalMaterial.headerRow.grossMarginPercentage;
    gm.mechanicalMaterialShippingHandlingGMPercent = mat.mechanicalMaterial.shippingAndHandling.grossMarginPercentage;
    gm.mechanicalMaterialWarrantyGMPercent = mat.mechanicalMaterial.warranty.grossMarginPercentage;

    gm.electricalMaterialGMPercent = mat.electricalMaterial.headerRow.grossMarginPercentage;
    gm.electricalMaterialShippingHandlingGMPercent = mat.electricalMaterial.shippingAndHandling.grossMarginPercentage;
    gm.electricalMaterialWarrantyGMPercent = mat.electricalMaterial.warranty.grossMarginPercentage;

    gm.materialCostGMPercent = mat.materialCostsHeader.grossMarginPercentage;

    // ── Labor GM% ───────────────────────────────────────────────
    const lab = model.laborCosts;
    gm.projectManagementGMPercent = lab.projectManagement.grossMarginPercentage;
    gm.engineeringGMPercent = lab.engineering.grossMarginPercentage;
    gm.nEngineeringGMPercent = lab.networkEngineering.grossMarginPercentage;
    gm.qEngineeringGMPercent = lab.qualificationEngineering.grossMarginPercentage;
    gm.cEngineeringGMPercent = lab.cyberSecurityEngineering.grossMarginPercentage;
    gm.eEngineeringGMPercent = lab.energyEngineering.grossMarginPercentage;
    gm.lEngineeringGMPercent = lab.electricalEngineering.grossMarginPercentage;
    gm.graphicsGMPercent = lab.graphics.grossMarginPercentage;
    gm.programmingGMPercent = lab.programming.grossMarginPercentage;
    gm.specialistGMPercent = lab.specialist.grossMarginPercentage;
    gm.digitalDeploymentGMPercent = lab.digitalDeploymentSpecialist.grossMarginPercentage;
    gm.mechanicalLaborGMPercent = lab.mechanical.grossMarginPercentage;
    gm.electricianGMPercent = lab.electrical.grossMarginPercentage;
    gm.laborCostGMPercent = lab.laborCostsHeader.grossMarginPercentage;

    // ── Subcontractor GM% ───────────────────────────────────────
    gm.subContractorCostGMPercent = model.subContractorCosts.headerRow.grossMarginPercentage;

    // ── Misc / Risk / Travel GM% ────────────────────────────────
    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;

    const readNamedRowGM = (rowName: string): number => {
      const row = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, rowName);
      return row ? row.grossMarginPercentage || CONSTANTS.ZERO : CONSTANTS.ZERO;
    };

    gm.miscellaneousCostGMPercent = readNamedRowGM('MISCELLANEOUS_COSTS');
    gm.productivityReserveGMPercent = readNamedRowGM('PRODUCTIVITY_RESERVE');
    gm.mechFeeCostGMPercent = readNamedRowGM('MECHANICAL_FEE');
    gm.subcontractorFeeCostGMPercent = readNamedRowGM('SUBCONTRACTOR_FEE');
    gm.travelExpenseGMPercent = readNamedRowGM('TRAVEL_EXPENSE_COSTS');
    gm.autoRiskCostGMPercent = readNamedRowGM('CALCULATED_RISK_COSTS');
    gm.riskCostGMPercent = readNamedRowGM('PROJECT_ENTERED_RISK_COSTS');

    // ── Standalone GM% ──────────────────────────────────────────
    gm.technologyFeeGMPercent = config.technologyFeeGMPercent;
    gm.subTotalGMPercent = model.subTotals.grossMarginPercentage;

    // ── Sold As GM% ─────────────────────────────────────────────
    gm.soldASGMPercent = config.soldAsGMPercent;
    gm.turboGMPercent = model.sellAs.turboGMPercent;

    this.commonDataService.patchOtherMetaData(otherMetaData, { silent: true });
  }

  /**
   * Writes per-row Markup % values from the model back into OtherMetaData.grossMargin.
   */
  private updateEstimateMarkupPerDTO(
    model: EstimateUiModel,
    config: SellPriceConfig
  ): void {
    // Zero cost zeroes the row margins; persisting the resulting 0% would
    // destroy the user's margins, so keep the previously stored percentages.
    if (this.shouldSkipGMPersistence(model, config)) return;

    const otherMetaData = this.commonDataService.getOtherMetaData();
    if (!otherMetaData?.grossMargin) return;
    const gm = otherMetaData.grossMargin;

    // ── Material GM% ────────────────────────────────────────────
    const mat = model.materialCosts;
    gm.siemensMaterialGMPercent = mat.siemensMaterial.headerRow.markUpPercentage;
    gm.siemensMaterialShippingHandlingGMPercent = mat.siemensMaterial.shippingAndHandling.markUpPercentage;
    gm.siemensMaterialWarrantyGMPercent = mat.siemensMaterial.warranty.markUpPercentage;

    gm.outsideMaterialGMPercent = mat.outsideMaterial.headerRow.markUpPercentage;
    gm.outsideMaterialShippingHandlingGMPercent = mat.outsideMaterial.shippingAndHandling.markUpPercentage;
    gm.outsideMaterialWarrantyGMPercent = mat.outsideMaterial.warranty.markUpPercentage;

    gm.mechanicalMaterialGMPercent = mat.mechanicalMaterial.headerRow.markUpPercentage;
    gm.mechanicalMaterialShippingHandlingGMPercent = mat.mechanicalMaterial.shippingAndHandling.markUpPercentage;
    gm.mechanicalMaterialWarrantyGMPercent = mat.mechanicalMaterial.warranty.markUpPercentage;

    gm.electricalMaterialGMPercent = mat.electricalMaterial.headerRow.markUpPercentage;
    gm.electricalMaterialShippingHandlingGMPercent = mat.electricalMaterial.shippingAndHandling.markUpPercentage;
    gm.electricalMaterialWarrantyGMPercent = mat.electricalMaterial.warranty.markUpPercentage;

    gm.materialCostGMPercent = mat.materialCostsHeader.markUpPercentage;

    // ── Labor GM% ───────────────────────────────────────────────
    const lab = model.laborCosts;
    gm.projectManagementGMPercent = lab.projectManagement.markUpPercentage;
    gm.engineeringGMPercent = lab.engineering.markUpPercentage;
    gm.nEngineeringGMPercent = lab.networkEngineering.markUpPercentage;
    gm.qEngineeringGMPercent = lab.qualificationEngineering.markUpPercentage;
    gm.cEngineeringGMPercent = lab.cyberSecurityEngineering.markUpPercentage;
    gm.eEngineeringGMPercent = lab.energyEngineering.markUpPercentage;
    gm.lEngineeringGMPercent = lab.electricalEngineering.markUpPercentage;
    gm.graphicsGMPercent = lab.graphics.markUpPercentage;
    gm.programmingGMPercent = lab.programming.markUpPercentage;
    gm.specialistGMPercent = lab.specialist.markUpPercentage;
    gm.digitalDeploymentGMPercent = lab.digitalDeploymentSpecialist.markUpPercentage;
    gm.mechanicalLaborGMPercent = lab.mechanical.markUpPercentage;
    gm.electricianGMPercent = lab.electrical.markUpPercentage;
    gm.laborCostGMPercent = lab.laborCostsHeader.markUpPercentage;

    // ── Subcontractor GM% ───────────────────────────────────────
    gm.subContractorCostGMPercent = model.subContractorCosts.headerRow.markUpPercentage;

    // ── Misc / Risk / Travel GM% ────────────────────────────────
    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;

    const readNamedRowGM = (rowName: string): number => {
      const row = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, rowName);
      return row ? row.markUpPercentage || CONSTANTS.ZERO : CONSTANTS.ZERO;
    };

    gm.miscellaneousCostGMPercent = readNamedRowGM('MISCELLANEOUS_COSTS');
    gm.productivityReserveGMPercent = readNamedRowGM('PRODUCTIVITY_RESERVE');
    gm.mechFeeCostGMPercent = readNamedRowGM('MECHANICAL_FEE');
    gm.subcontractorFeeCostGMPercent = readNamedRowGM('SUBCONTRACTOR_FEE');
    gm.travelExpenseGMPercent = readNamedRowGM('TRAVEL_EXPENSE_COSTS');
    gm.autoRiskCostGMPercent = readNamedRowGM('CALCULATED_RISK_COSTS');
    gm.riskCostGMPercent = readNamedRowGM('PROJECT_ENTERED_RISK_COSTS');

    // ── Standalone GM% ──────────────────────────────────────────
    gm.technologyFeeGMPercent = config.technologyFeeGMPercent;
    gm.subTotalGMPercent = model.subTotals.markUpPercentage;

    // ── Sold As GM% ─────────────────────────────────────────────
    gm.soldASGMPercent = config.soldAsGMPercent;
    gm.turboGMPercent = model.sellAs.turboGMPercent;

    this.commonDataService.patchOtherMetaData(otherMetaData, { silent: true });
  }

  /**
   * Writes per-row sell price values from the model back into OtherMetaData.sellPrice.
   */
  private updateEstimateSellPriceDTO(model: EstimateUiModel): void {
    const otherMetaData = this.commonDataService.getOtherMetaData();
    if (!otherMetaData) return;

    if (!otherMetaData.sellPrice) {
      otherMetaData.sellPrice = new EstimateSellPrice();
    }
    const sp = otherMetaData.sellPrice;

    // ── Material sell prices ────────────────────────────────────
    const mat = model.materialCosts;
    sp.siemensMaterialSellPrice = mat.siemensMaterial.headerRow.sellPrice;
    sp.siemensMaterialSHSellPrice = mat.siemensMaterial.shippingAndHandling.sellPrice;
    sp.siemensMaterialWarrantySellPrice = mat.siemensMaterial.warranty.sellPrice;

    sp.outsideMaterialSellPrice = mat.outsideMaterial.headerRow.sellPrice;
    sp.outsideMaterialSHSellPrice = mat.outsideMaterial.shippingAndHandling.sellPrice;
    sp.outsideMaterialWarrantySellPrice = mat.outsideMaterial.warranty.sellPrice;

    sp.mechMaterialSellPrice = mat.mechanicalMaterial.headerRow.sellPrice;
    sp.mechMaterialSHSellPrice = mat.mechanicalMaterial.shippingAndHandling.sellPrice;
    sp.mechMaterialWarrantySellPrice = mat.mechanicalMaterial.warranty.sellPrice;

    sp.electMaterialSellPrice = mat.electricalMaterial.headerRow.sellPrice;
    sp.elecMaterialSHSellPrice = mat.electricalMaterial.shippingAndHandling.sellPrice;
    sp.elecMaterialWarrantySellPrice = mat.electricalMaterial.warranty.sellPrice;

    // ── Labor sell prices ───────────────────────────────────────
    const lab = model.laborCosts;
    sp.pmSellPrice = lab.projectManagement.sellPrice;
    sp.engSellPrice = lab.engineering.sellPrice;
    sp.nEngineeringSellPrice = lab.networkEngineering.sellPrice;
    sp.qEngineeringSellPrice = lab.qualificationEngineering.sellPrice;
    sp.cEngineeringSellPrice = lab.cyberSecurityEngineering.sellPrice;
    sp.eEngineeringSellPrice = lab.energyEngineering.sellPrice;
    sp.lEngineeringSellPrice = lab.electricalEngineering.sellPrice;
    sp.grapSellPrice = lab.graphics.sellPrice;
    sp.progSellPrice = lab.programming.sellPrice;
    sp.specSellPrice = lab.specialist.sellPrice;
    sp.ddepSellPrice = lab.digitalDeploymentSpecialist.sellPrice;
    sp.mechSellPrice = lab.mechanical.sellPrice;
    sp.elecSellPrice = lab.electrical.sellPrice;

    // ── Misc / Risk / Travel sell prices ────────────────────────
    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;

    const readNamedRowSP = (rowName: string): number => {
      const row = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, rowName);
      return row ? row.sellPrice : CONSTANTS.ZERO;
    };

    sp.miscSellPrice = readNamedRowSP('MISCELLANEOUS_COSTS');
    sp.productivityReserveSellPrice = readNamedRowSP('PRODUCTIVITY_RESERVE');
    sp.subcontractorFeeCostSellPrice = readNamedRowSP('SUBCONTRACTOR_FEE');
    sp.travelSellPrice = readNamedRowSP('TRAVEL_EXPENSE_COSTS');
    sp.autoRiskCostSellPrice = readNamedRowSP('CALCULATED_RISK_COSTS');
    sp.riskCostSellPrice = readNamedRowSP('PROJECT_ENTERED_RISK_COSTS');
    sp.mechFeeCostSellPrice = readNamedRowSP('MECHANICAL_FEE');

    this.commonDataService.patchOtherMetaData(otherMetaData, { silent: true });
  }

  /**
   * Writes flags and aggregate sell prices from the model back into
   * EstimateMetaData.estimateTab.
   */
  private updateEstimateTabDTO(
    model: EstimateUiModel,
    config: SellPriceConfig
  ): void {
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const estimateData = this.commonDataService.getEstimateData();
    if (!estimateMetaData?.estimateTab) return;
    const tab = estimateMetaData.estimateTab;

    // ── Flags ───────────────────────────────────────────────────
    tab.isMarkUpSelected = config.isMarkUpSelected;
    estimateData.isMarkUpSelected = config.isMarkUpSelected;
    tab.isFixedPriceSelected = config.isFixedPriceSelected;

    // ── Sell As sell price ───────────────────────────────────────
    tab.soldASSellPrice = model.sellAs.sellAsSellPrice;
    estimateData.sellPrice = model.sellAs.sellAsSellPrice;

    // ── Estimate-level cost / margin (mirrors backend HarmonizeEstimateDataWithProjectObject) ──
    estimateData.subCost = model.subTotals.cost;
    estimateData.marginPercent = config.soldAsGMPercent;

    // ── Material sell prices (aggregated) ────────────────────────
    tab.materialCostSellPrice = model.materialCosts.materialCostsHeader.sellPrice;

    // ── Sub-totals sell price ────────────────────────────────────
    tab.subTotalSellprice = model.subTotals.sellPrice;

    // ── Technology fee sell price ────────────────────────────────
    tab.technologyFeeSellprice = model.technologyFee.sellPrice;

    // ── Subcontractor sell price (sum of all rows) ──────────────
    tab.subContractorCostItemSellPrice = CONSTANTS.ZERO;
    // estimateMetaData.estimateTab.estimateTabSubContractors = [];
    for (const row of model.subContractorCosts.subContractorsCostsRow) {
      tab.subContractorCostItemSellPrice += row.sellPrice;
      const subcontractor = estimateMetaData.estimateTab.estimateTabSubContractors.find(subcontractor => subcontractor.description?.toLowerCase() === row.name?.toLowerCase());
      if (subcontractor) {
        subcontractor.sellPrice = row.sellPrice;
        subcontractor.gmPercent = config.isMarkUpSelected ? row.markUpPercentage : row.grossMarginPercentage;
        subcontractor.isLastModifiedSubContSellPrice = row.isSellPriceLastModified;
      }
    }

    // ── Tax cost ────────────────────────────────────────────────
    const taxTypeID = config.taxTypeID;
    if (taxTypeID === TaxTypes.SalesTax) {
      tab.taxCost = model.taxMaterialLaborRate.headerRow.sellPrice;
    } else if (taxTypeID === TaxTypes.UseTax || taxTypeID === TaxTypes.HST || taxTypeID === TaxTypes.PST) {
      tab.taxCost = model.taxMaterialRate.headerRow.cost;
    } else {
      tab.taxCost = model.taxNotIncluded.cost;
    }

    // ── Misc costs ──────────────────────────────
    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;

    const readNamedRowCost = (rowName: string): number => {
      const row = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, rowName);
      return row ? row.cost : CONSTANTS.ZERO;
    };

    tab.subcontractorFeeCost = readNamedRowCost('SUBCONTRACTOR_FEE');
    tab.autoRiskCost = readNamedRowCost('CALCULATED_RISK_COSTS');
    tab.mechFeeCost = readNamedRowCost('MECHANICAL_FEE');
    tab.productivityReserveCost = readNamedRowCost('PRODUCTIVITY_RESERVE');
    tab.technologyFeeCost = model.technologyFee.cost;

    this.commonDataService.patchEstimateMetaData(estimateMetaData);
  }
}
